/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import _ from 'lodash';

import Session from '@/Session';
import lazyLoadView from '@/util/lazyLoadView';
import SideNavSectionView from '@/views/SideNavSectionView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import AWPackageNoteFileUploadTmpl from '@/fileupload/AWPackageNoteFileUploadTmpl.hbs';
import showModal from '@/util/showModal';
import SearchEPCUsersModalView from '@/views/SearchEPCUsersModalView/SearchEPCUsersModalView';

import LegacyAWPackageHeaderView from '../../views/LegacyAWPackageHeaderView';
import loadLegacyAWPackageInformationView from '../../loaders/LegacyAWPackageInformationViewLoader';
import loadAWPackageDeliveryScheduleView from '../../loaders/AWPackageDeliveryScheduleViewLoader';
import loadAWPackageSelectOPsView from '../../loaders/AWPackageSelectOPsViewLoader';
import loadAWPackageAttachmentsView from '../../loaders/AWPackageAttachmentsViewLoader';
import loadAWPackageNotesView from '../../loaders/AWPackageNotesViewLoader';
import GetAWPackageNotes from '../../services/GetAWPackageNotes';
import SubmitAWPackageNote from '../../services/SubmitAWPackageNote';
import SubmitAWPackage from '../../services/SubmitAWPackage';
import ReassignAWPackage from '../../services/ReassignAWPackage';
import HideAWPackage from '../../services/HideAWPackage';
import DeleteAWPackage from '../../services/DeleteAWPackage';
import AWPackageAccess from '../../utils/AWPackageAccess';
import GetPackagedOrderPackagesByAWPackageID from '../../services/GetPackagedOrderPackagesByAWPackageID';

import './LegacyAWPackagePage.css';

import tmplLegacyAWPackagePage from './LegacyAWPackagePage.hbs';

const LegacyAWPackagePage = View.extend({
  template: tmplLegacyAWPackagePage,

  className: 'aw-package-page container-fluid',

  ui: {
    headerBar: '.header-bar-container',
    sections: '.sections-container'
  },

  regions: {
    headerBar: '@ui.headerBar',
    sections: '@ui.sections'
  },

  childViewEvents: {
    reload: 'reloadEditable',
    'reassign:epc': 'onReassignEPC',
    'delete:draft': 'onDeleteDraft',
    hide: 'onClickHide'
  },

  onRender() {
    this.showHeaderView();
    this.showSideNavSectionView();
  },

  showHeaderView() {
    const awPackage = this.model.get('awPackage');
    this.showChildView(
      'headerBar',
      new LegacyAWPackageHeaderView({
        showViewEditBtn: AWPackageAccess.isEditButtonVisible(this.options.isAssignedEPC, awPackage.statusId),
        showReassignEPCBtn: AWPackageAccess.isReassignable(this.options.isAssignedEPC, awPackage.statusId),
        showDeleteButton: AWPackageAccess.isDeleteable(awPackage.statusId, this.options.isAssignedEPC),
        showHideButton: AWPackageAccess.isHideButtonVisible(awPackage.statusId),
        showPrintButton: true,
        isEditing: false,
        model: new Backbone.Model(awPackage)
      })
    );
  },

  showSideNavSectionView() {
    const awPackage = this.model.get('awPackage');
    const awPackageId = awPackage.id;
    const statusId = awPackage.statusId;
    const activeSectionId = this.options.activeSectionId;
    let nextStatusName = this.getNextStatusName(statusId);
    nextStatusName === 'Sent to Contracting' ? (nextStatusName = 'Send to Contracting') : nextStatusName;
    const isEPCOwner = this.model.get('awPackage')?.epcId === Session.user.id;
    const migrated = awPackage.isMigrated === '1';
    const isEditing = this.isEditing === true || (this.isEditing === undefined && statusId === '1');

    //Determine user access to AW Package based on role, ownership of order package, status, and tab
    const isEditable = AWPackageAccess.isEditable(isEPCOwner, isEditing, statusId);
    const isNotesEditable = AWPackageAccess.isNotesEditable(statusId);
    const isSavable = AWPackageAccess.isSavable(isEPCOwner, isEditing, statusId);
    const isSubmittable = AWPackageAccess.isSubmittable(isEPCOwner, statusId);

    this.sidenavSectionView = new SideNavSectionView({
      useNavigationButtons: true,
      editable: isEditable,
      activeSectionId,
      onSubmit: this.onSubmit.bind(this),
      model: new Backbone.Model({
        returnLabel: 'Return to Dashboard',
        returnLink: '#dashboard',
        submitButtonText: `Submit to ${nextStatusName}`,
        migrated
      }),
      collection: new Backbone.Collection([
        {
          id: 'orderPackages',
          title: 'Order Packages',
          editable: isEditable,
          isSavable,
          showSubmitButton: isSubmittable,
          loader: loadAWPackageSelectOPsView,
          loaderOptions: { awPackageId, editable: isEditable },
          view: lazyLoadView(() => loadAWPackageSelectOPsView({ awPackageId, editable: isEditable }))
        },
        {
          id: 'deliverySchedule',
          title: 'Delivery Schedule',
          editable: isEditable,
          isSavable: false,
          showSubmitButton: isSubmittable,
          loader: loadAWPackageDeliveryScheduleView,
          loaderOptions: { awPackageId, editable: isEditable },
          view: lazyLoadView(() => loadAWPackageDeliveryScheduleView({ awPackageId, editable: isEditable }))
        },
        {
          id: 'attachments',
          title: 'Attachments',
          editable: isEditable,
          isSavable: false,
          showSubmitButton: isSubmittable,
          loader: loadAWPackageAttachmentsView,
          loaderOptions: { awPackageId, editable: isEditable },
          view: lazyLoadView(() => loadAWPackageAttachmentsView({ awPackageId, editable: isEditable })),
          tooltip: {
            text: 'Templates for the required forms are available by clicking the name of the form on the left if needed.\n\nAccepted attachment types are: .doc, .docx, .xls, .xlsx, .xlsm, .pdf, and .msg.',
            placement: 'right'
          }
        },
        {
          id: 'awPackageInformation',
          title: 'AW Package Information',
          editable: isEditable,
          isSavable,
          showSubmitButton: isSubmittable,
          loader: loadLegacyAWPackageInformationView,
          view: lazyLoadView(() => loadLegacyAWPackageInformationView({ awPackageId, editable: isEditable }))
        },
        {
          id: 'notes',
          title: 'AW Package Notes',
          editable: isNotesEditable,
          isSaveable: false,
          showSubmitButton: isSubmittable,
          loader: loadAWPackageNotesView,
          loaderOptions: {
            editable: isNotesEditable,
            metadata: {
              awPackageId
            },
            objectId: awPackageId,
            getNotesService: GetAWPackageNotes,
            submitNoteService: SubmitAWPackageNote,
            submitNoteAttachmentService: AWPackageNoteFileUploadTmpl,
            pathToFileId: 'html.head.script.Envelope.Body.UploadAWPackageNoteAttachmentResponse.AttachmentID.__text'
          },
          view: lazyLoadView(() =>
            loadAWPackageNotesView({
              editable: isNotesEditable,
              metadata: {
                awPackageId
              },
              objectId: awPackageId,
              getNotesService: GetAWPackageNotes,
              submitNoteService: SubmitAWPackageNote,
              submitNoteAttachmentService: AWPackageNoteFileUploadTmpl,
              pathToFileId: 'html.head.script.Envelope.Body.UploadAWPackageNoteAttachmentResponse.AttachmentID.__text'
            })
          )
        }
      ])
    });

    this.showChildView('sections', this.sidenavSectionView);

    // Set views to validate
    // const renderedModels = _.filter(sidenavSectionView.collection.models, (model) => model.get('renderable'));
    // const validatedViews = _.map(renderedModels, (model) => model.get('view'));
    // ValidateAWPModule.views = validatedViews;
  },

  getNextStatusName(statusId) {
    // statuses are 0-indexed, but statusId is from the db, and is 1-indexed,
    //  so we don't have to increment to get the next status
    const statuses = this.model.get('awStatuses');
    const nextStatusId = `${parseInt(statusId) + 1}`;
    return _.find(statuses, { id: nextStatusId })?.awPackageStatus;
  },

  async validate(awPackageId) {
    let isValid = true;
    for (const model of this.sidenavSectionView.collection.models) {
      const view = model.get('view');
      const viewValid = await view.call('validate');
      isValid = viewValid !== false && isValid;
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');

    if (isValid) {
      const packagedOrderPackages = await GetPackagedOrderPackagesByAWPackageID(awPackageId);
      const hasPackagedOrderPackages = packagedOrderPackages.length > 0;

      if (!hasPackagedOrderPackages) this.showNoOrderPackagesError();
      isValid = hasPackagedOrderPackages;
    } else {
      this.showSubmissionError(awPackageId);
    }

    return isValid;
  },

  showHideSuccessMessage() {
    showSuccessMessage({
      code: 'hideSuccess',
      title: 'Hide Success',
      message: 'AW Package Successfully Hidden.',
      hideAfter: 10
    });
  },

  showHideErrorMessage() {
    showErrorMessage({
      errorCode: 'hideError',
      errorTitle: 'Hide Error',
      hideAfter: 10,
      errorMessage: 'An error occurred during hiding. Try again or contact an administrator if your issue persists.'
    });
  },

  showSubmissionError(packageId) {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: `There was an error submitting AW Package ${packageId}. Please try again or contact an administrator if the issue persists.`
    });
  },

  showSubmissionSuccess() {
    showSuccessMessage({
      code: 'submitSuccess',
      title: 'Submit Success',
      message: 'AW Package Submitted.'
    });
  },

  showReassignmentErrorMessage() {
    showErrorMessage({
      errorCode: 'reassignmentError',
      errorTitle: 'Reassignment Error',
      errorMessage:
        'An error occurred during reassignment. Try again or contact an administrator if your issue persists.'
    });
  },

  showNoOrderPackagesError() {
    showErrorMessage({
      errorCode: 'noOrdersPackagesError',
      errorTitle: 'No Order Packages Added',
      errorMessage: 'You must add one or more order packages to the AW package before submitting.'
    });
  },

  showDraftDeletedErrorMessage() {
    showErrorMessage({
      errorCode: 'draftDeletedError',
      errorTitle: 'Draft Deleted Error',
      errorMessage: 'An error occurred during deletion. Try again or contact an administrator if your issue persists.'
    });
  },

  showDraftDeletedSuccessMessage() {
    showSuccessMessage({
      code: 'deleteSuccess',
      title: 'Delete Success',
      message: 'AW Package Draft Deleted.',
      hideAfter: 5
    });
  },
  getActiveSectionId() {
    let activeSectionID = undefined;
    try {
      const sections = this.getChildView('sections');
      const activeLinkIndex = parseInt(sections.linkCollectionView.getActiveLinkIndex());
      const activeModel = sections.linkCollectionView.collection.at(activeLinkIndex);
      activeSectionID = activeModel.get('id');
    } catch (err) {
      // Ignore
    }
    return activeSectionID;
  },

  async onReassignEPC() {
    const user = await showModal({
      title: 'Reassign EPC',
      view: new SearchEPCUsersModalView(),
      dialogClasses: 'modal-lg'
    });

    try {
      await ReassignAWPackage({
        awPackageId: this.model.get('awPackage')?.id,
        internalUserId: user.id
      });
      this.getChildView('headerBar').setAssignedEPC(user.userName);
    } catch (err) {
      this.showReassignmentErrorMessage();
    }
  },

  async onDeleteDraft() {
    try {
      await DeleteAWPackage(this.model.get('awPackage')?.id);
      this.showDraftDeletedSuccessMessage();
      Radio.channel('navigate').request('show:int:dashboard');
    } catch (err) {
      this.showDraftDeletedErrorMessage();
    }
  },
  async onSubmit() {
    const awPackageId = this.model.get('awPackage')?.id;

    if (!(await this.validate(awPackageId))) {
      return;
    }

    try {
      await SubmitAWPackage(awPackageId);
      this.showSubmissionSuccess();
      await Radio.channel('navigate').request('show:int:dashboard');
    } catch (err) {
      this.showSubmissionError(awPackageId);
    }
  },

  async onClickHide() {
    try {
      await HideAWPackage(this.model.get('awPackage')?.id);
      const awPackageHeaderView = this.getChildView('headerBar');
      const hideButton = awPackageHeaderView.options.columns.where({ id: 'hideButton' });
      awPackageHeaderView.options.columns.remove(hideButton);
      awPackageHeaderView.model.set('awPackageStatus', 'Hidden');
      awPackageHeaderView.options.showHideButton = false;
      this.showHideSuccessMessage();
    } catch (err) {
      this.showHideErrorMessage();
    }
  },

  reloadEditable(isEditing) {
    this.options.activeSectionId = this.getActiveSectionId();
    this.isEditing = isEditing;
    this.showSideNavSectionView();
  }
});

export default LegacyAWPackagePage;
